/* =========================================================================
   Feast daily moment (§8). Flow: a 30-second story card (from getFeastMoment for
   the ET date; 200-null / offline / 503 → a bundled generic line and NO quiz) →
   an optional one-question quiz (reveal-style: correct = warm Theo 'feast-done'
   + small confetti; wrong = a gentle correction showing the right answer, never
   money) → a Feast Days BADGE earned into the local collection ('catholics.feasts')
   and, for accounts, granted server-side (grantFeastBadge with the db/016 feast
   key). Earning fires FeastVisited{feastId}, which completes the daily_feast quest
   and advances the Pilgrimage 'Meeting a Saint' step automatically.

   Revisiting an already-earned feast the same day shows the story + earned badge
   with NO double telemetry.
   ========================================================================= */
import { esc } from './engine';
import { icon, type IconName } from './ui/icons';
import { byId, show } from './dom';
import { track } from './telemetry';
import { getLine } from './characters/registry';
import { etDateString } from './today';
import { getFeastMoment, grantFeastBadge, type FeastMoment } from './api';
import { earnFeastBadge, feastKey, hasFeastBadge } from './feasts';
import { openCollections } from './collections';
import { hasOptionalMedia } from './featureFlags';

const FALLBACK_LINE =
  'Every day the Church remembers her saints and mysteries. Come back through the day and we will keep the feasts together.';

let mediaAvail = false;
let current: FeastMoment | null = null;

function probeMedia(): void {
  if (!hasOptionalMedia()) return; // feast-frame.mp4 not installed yet — CSS fallback
  try {
    const v = document.createElement('video');
    v.muted = true;
    v.preload = 'metadata';
    v.onloadeddata = () => {
      mediaAvail = true;
    };
    v.onerror = () => {};
    v.src = 'media/feast-frame.mp4';
  } catch {
    /* no video support — styled card fallback */
  }
}

function storyBackdrop(): string {
  return mediaAvail
    ? `<video class="feast-frame-video" src="media/feast-frame.mp4" autoplay loop muted playsinline></video>`
    : `<div class="feast-frame-fallback" aria-hidden="true"></div>`;
}

/** Small celebratory confetti (reuses the .rc / rcFall round-intro pattern). */
function celebrate(): void {
  const wrap = document.getElementById('feastConfetti');
  if (!wrap) return;
  wrap.innerHTML = '';
  const marks: IconName[] = ['sparkle', 'star', 'dove'];
  for (let i = 0; i < 14; i++) {
    const s = document.createElement('div');
    s.className = 'rc';
    s.innerHTML = icon(marks[i % marks.length], 15 + Math.round(Math.random() * 16));
    s.style.left = (5 + Math.random() * 90).toFixed(1) + '%';
    s.style.animationDelay = (Math.random() * 0.55).toFixed(2) + 's';
    s.style.setProperty('--rcr', (Math.random() * 360 - 180).toFixed(0) + 'deg');
    wrap.appendChild(s);
  }
}

/* -------------------------------- rendering -------------------------------- */

function renderNoFeast(): void {
  byId('feastSub').textContent = '';
  byId('feastBody').innerHTML =
    `<div class="feast-card">` +
    storyBackdrop() +
    `<div class="feast-card-inner">` +
    `<div class="feast-theo">${esc(getLine('theo', 'feast-intro'))}</div>` +
    `<div class="feast-story">${esc(FALLBACK_LINE)}</div>` +
    `</div></div>`;
}

function renderStory(m: FeastMoment): void {
  byId('feastSub').textContent = m.title;
  const already = hasFeastBadge(m.feastDayId);
  const cta = m.quiz
    ? `<button class="btn feast-cta" id="feastQuizBtn" type="button">A little question…</button>`
    : `<button class="btn feast-cta" id="feastBadgeBtn" type="button">${already ? 'Keep this feast' : 'Receive today’s badge'}</button>`;
  byId('feastBody').innerHTML =
    `<div class="feast-card">` +
    storyBackdrop() +
    `<div class="feast-card-inner">` +
    `<div class="feast-theo">${esc(getLine('theo', 'feast-intro'))}</div>` +
    `<div class="feast-name">${esc(m.title)}</div>` +
    `<div class="feast-story">${esc(m.storyText || `Today the Church honors ${m.title}.`)}</div>` +
    (already ? `<div class="feast-earned-chip">✓ Badge earned. A blessed feast to you.</div>` : '') +
    cta +
    `</div></div>`;
  if (m.quiz) byId('feastQuizBtn').addEventListener('click', () => renderQuiz(m));
  else byId('feastBadgeBtn').addEventListener('click', () => earnBadge(m));
}

function renderQuiz(m: FeastMoment): void {
  const quiz = m.quiz!;
  const opts = quiz.options
    .map((o, i) => `<button class="feast-opt" type="button" data-idx="${i}">${esc(o)}</button>`)
    .join('');
  byId('feastBody').innerHTML =
    `<div class="feast-card">` +
    storyBackdrop() +
    `<div class="feast-card-inner">` +
    `<div class="feast-q">${esc(quiz.q)}</div>` +
    `<div class="feast-opts" id="feastOpts">${opts}</div>` +
    `<div class="feast-quiz-result hidden" id="feastQuizResult"></div>` +
    `</div></div>`;
  byId('feastOpts')
    .querySelectorAll<HTMLButtonElement>('.feast-opt')
    .forEach((b) => b.addEventListener('click', () => answerQuiz(m, Number(b.dataset.idx))));
}

function answerQuiz(m: FeastMoment, idx: number): void {
  const quiz = m.quiz!;
  const correct = idx === quiz.answerIdx;
  const opts = byId('feastOpts');
  opts.querySelectorAll<HTMLButtonElement>('.feast-opt').forEach((b, i) => {
    b.disabled = true;
    if (i === quiz.answerIdx) b.classList.add('correct');
    else if (i === idx) b.classList.add('wrong');
  });
  const result = byId('feastQuizResult');
  result.classList.remove('hidden');
  if (correct) {
    result.innerHTML = `<div class="fqr-good">${esc(getLine('theo', 'feast-done'))}</div>`;
    celebrate();
  } else {
    // Gentle correction — NO money, just the right answer and a kind word.
    result.innerHTML =
      `<div class="fqr-gentle">Not quite. The answer is <b>${esc(quiz.options[quiz.answerIdx])}</b>. ` +
      `No harm done; every question teaches us something.</div>`;
  }
  const btn = document.createElement('button');
  btn.className = 'btn feast-cta';
  btn.textContent = hasFeastBadge(m.feastDayId) ? 'Keep this feast' : 'Receive today’s badge';
  btn.addEventListener('click', () => earnBadge(m));
  result.appendChild(btn);
}

function earnBadge(m: FeastMoment): void {
  const date = etDateString();
  const first = earnFeastBadge(m.feastDayId, m.title, date);
  if (first) {
    // Fire telemetry + server grant EXACTLY once (never on a revisit).
    track('FeastVisited', { feastId: m.feastDayId });
    void grantFeastBadge({ feastKey: feastKey(m.feastDayId) });
    celebrate();
  }
  byId('feastBody').innerHTML =
    `<div class="feast-card">` +
    storyBackdrop() +
    `<div class="feast-card-inner feast-badge-earned">` +
    `<div class="feast-badge-ic">${icon('sparkle', 40)}</div>` +
    `<div class="feast-name">${esc(m.title)}</div>` +
    `<div class="feast-earned-chip big">✓ Feast Days badge ${first ? 'earned' : 'kept'}</div>` +
    `<div class="feast-theo">${esc(getLine('theo', 'feast-done'))}</div>` +
    `<button class="btn secondary" id="feastToCollections" type="button">See your Feast Days →</button>` +
    `</div></div>`;
  byId('feastToCollections').addEventListener('click', () => void openCollections());
}

/* -------------------------------- public -------------------------------- */
export async function openFeastMoment(): Promise<void> {
  track('AreaOpened', { area: 'feast' });
  byId('bankbar').classList.add('hidden');
  show('feast');
  byId('feastBody').innerHTML = `<div class="feast-card"><div class="feast-card-inner"><div class="feast-story">Loading today’s feast…</div></div></div>`;
  const r = await getFeastMoment(etDateString());
  if (r.ok && r.data) {
    current = r.data;
    renderStory(r.data);
  } else {
    current = null;
    renderNoFeast();
  }
}

export function initFeast(): void {
  probeMedia();
  byId('feastBackBtn').addEventListener('click', () => {
    byId('bankbar').classList.add('hidden');
    show('home');
  });
}

/** Exposed for tests/telemetry inspection. */
export function currentFeast(): FeastMoment | null {
  return current;
}
